import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  styled,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  SubdirectoryArrowLeft as SubdirectoryArrowLeftIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';
import { getProjectHistory, deleteProject, updateProject } from '../services/api';
import { getUserData } from 'utils/encryption';
import { error } from 'utils/logger';

const DRAWER_WIDTH = 240;
const COLLAPSED_DRAWER_WIDTH = 68;

interface HistoryItem {
  id: number;
  scan_type: string;
  status: string;
  created_at: string;
}

interface ProjectOverviewProps {
  projectId: number;
  projectName: string;
  repositoryUrl: string;
  createdAt: string;
  updatedAt: string;
  description: string;
  tags: string[];
  lastScanDate?: string;
  auth_id: number;
}

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 'auto',
  padding: '12px 16px',
  fontWeight: 500,
  fontSize: '1rem',
  color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
  '&.Mui-selected': {
    color: '#FF4800',
    backgroundColor: 'transparent',
    fontWeight: 500,
  },
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#FF4800',
  },
}));

const StyledTabs = styled(Tabs)({
  minHeight: 'auto',
  '& .MuiTabs-indicator': {
    backgroundColor: '#FF4800',
    height: '2px',
  },
});

const StyledInput = styled('input')<{ type?: string; width?: string }>(({ theme, width = '350px' }) => ({
  width,
  padding: '8px 12px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB'}`,
  borderRadius: '6px',
  fontSize: '1rem',
  color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#111827',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.6)' : 'white',
  '&:focus': {
    outline: 'none',
    borderColor: '#FF4800',
    boxShadow: '0 0 0 2px rgba(255, 72, 0, 0.1)',
  }
}));

const StyledTextArea = styled('textarea')(({ theme }) => ({
  width: '100%',
  maxWidth: '700px',
  padding: '8px 12px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB'}`,
  borderRadius: '6px',
  fontSize: '1rem',
  color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#111827',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.6)' : 'white',
  resize: 'vertical',
  minHeight: '100px',
  '&:focus': {
    outline: 'none',
    borderColor: '#FF4800',
    boxShadow: '0 0 0 2px rgba(255, 72, 0, 0.1)',
  }
}));

const StyledTagInput = styled('input')(({ theme }) => ({
  flex: 1,
  border: 'none',
  padding: '8px',
  backgroundColor: 'transparent',
  color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#111827',
  fontSize: '0.875rem',
  '&:focus': {
    outline: 'none'
  },
  '&::placeholder': {
    color: theme.palette.mode === 'dark' ? '#94A3B8' : '#9CA3AF'
  }
}));


const Tag = styled(Chip)(({ theme }) => ({
  height: '35px',
  borderRadius: '999px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#E8F0FE',
  color: theme.palette.mode === 'dark' ? '#94A3B8' : '#1967D2',
  fontSize: '0.8rem',
  margin: '0 4px 4px 0',
  fontWeight: 500,
  border: 'none',
  padding: '0 5px',
  '& .MuiChip-label': {
    padding: '0 8px',
    lineHeight: '22px'
  },
  '& .MuiChip-deleteIcon': {
    fontSize: '14px',
    color: theme.palette.mode === 'dark' ? '#94A3B8' : '#1967D2',
    margin: '0 4px 0 -4px',
    '&:hover': {
      color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#1557B0'
    }
  }
}));

const DeleteButton = styled(Button)(({ theme }) => ({
    backgroundColor: '#FF4800',
    color: 'white',
    textTransform: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    '& .MuiSvgIcon-root': {
      filter: `drop-shadow(0 0 8px ${theme.customPalette.glow.primary})`
    }
  }));

const ProjectOverview: React.FC<ProjectOverviewProps> = ({
  projectId,
  projectName,
  repositoryUrl,
  createdAt,
  updatedAt,
  description,
  tags,
  lastScanDate,
  auth_id
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen } = useSidebar();
  const currentUserId = (getUserData() || '{}').user_id;
  const canModify = currentUserId.id === auth_id;
  const [currentTab, setCurrentTab] = useState(0);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState(false);
  const [confirmSaveDialog, setConfirmSaveDialog] = useState(false);
  const [editedData, setEditedData] = useState({
    projectName,
    description,
    repositoryUrl,
    tags
  });
  const [validationErrors, setValidationErrors] = useState<{
    projectName?: string;
  }>({});

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const data = await getProjectHistory(projectId);
        setHistory(data);
        } catch (err : any) {
        error('Error loading history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [projectId]);

  const hasUnsavedChanges = () => {
    return (
      editedData.projectName !== projectName ||
      editedData.description !== description ||
      editedData.repositoryUrl !== repositoryUrl ||
      JSON.stringify(editedData.tags) !== JSON.stringify(tags)
    );
  };

  const handleTabChangeWithCheck = (event: React.SyntheticEvent, newValue: number) => {
    if (hasUnsavedChanges()) {
      setUnsavedChangesDialog(true);
    } else {
      handleTabChange(event, newValue);
    }
  };

  const handleNavigateWithCheck = () => {
    if (hasUnsavedChanges()) {
      setUnsavedChangesDialog(true);
    } else {
      navigate(-1);
    }
  };

  const handleSave = () => {
    // Validate project name
    if (!editedData.projectName.trim()) {
      setValidationErrors({ projectName: "Project name is required" });
      return;
    }
    if (!/^[a-zA-Z0-9-_\s]+$/.test(editedData.projectName)) {
      setValidationErrors({ projectName: "Project name can only contain letters, numbers, dashes, and underscores" });
      return;
    }
    if (editedData.projectName.length > 50) {
      setValidationErrors({ projectName: "Project name cannot exceed 50 characters" });
      return;
    }

    // Validate tags
    const invalidTag = editedData.tags.find(tag => tag.length > 20);
    if (invalidTag) {
      const errorElement = document.getElementById('tag-error');
      if (errorElement) {
        errorElement.textContent = 'Tag name cannot exceed 20 characters';
      }
      return;
    }
    
    // Only open save dialog if all validations pass
    if (!validationErrors.projectName) {
      setConfirmSaveDialog(true);
    }
  };

  const handleConfirmSave = async () => {
    // Validate before saving
    if (!editedData.projectName.trim()) {
      setValidationErrors({ projectName: "Project name is required" });
      return;
    }
    if (!/^[a-zA-Z0-9-_\s]+$/.test(editedData.projectName)) {
      setValidationErrors({ projectName: "Project name can only contain letters, numbers, dashes, and underscores" });
      return;
    }
    if (editedData.projectName.length > 50) {
      setValidationErrors({ projectName: "Project name cannot exceed 50 characters" });
      return;
    }

    setSaveLoading(true);
    try {
      await updateProject(projectId, {
        name: editedData.projectName,
        description: editedData.description,
        tags: editedData.tags
      });
      setIsEditing(false);
      setConfirmSaveDialog(false);
      
      // Update the location state with new data
      const state = location.state || {};
      navigate(`/project/${projectId}`, {
        state: {
          ...state,
          projectName: editedData.projectName,
          description: editedData.description,
          tags: editedData.tags,
          updatedAt: new Date().toISOString()
        },
        replace: true
      });
    } catch (err) {
      error('Error updating project:', err);
      
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteProject(projectId);
      setDeleteDialogOpen(false);
      navigate(-1);
    } catch (err) {
      error('Error deleting project:', err);
      
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDiscard = () => {
    setEditedData({
      projectName,
      description,
      repositoryUrl,
      tags
    });
    setIsEditing(false);
    setUnsavedChangesDialog(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    if (isEditing) {
      setIsEditing(false);
      setEditedData({
        projectName,
        description,
        repositoryUrl,
        tags
      });
    }
  };

  const handleAddTag = (value: string) => {
    const errorElement = document.getElementById('tag-error');
    
    // Check if already at max tags limit
    if (editedData.tags.length >= 10) {
      if (errorElement) {
        errorElement.textContent = 'Maximum 10 tags allowed';
      }
      return false;
    }

    // Check tag length
    if (value.length > 20) {
      if (errorElement) {
        errorElement.textContent = 'Tag name cannot exceed 20 characters';
      }
      return false;
    }

    // Clear error when valid
    if (errorElement) {
      errorElement.textContent = '';
    }

    if (value && !editedData.tags.includes(value)) {
      setEditedData({
        ...editedData,
        tags: [...editedData.tags, value]
      });
      return true;
    }
    return false;
  };

  return (
    <Box sx={(theme) => ({ 
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.95)' : '#fdfdfd',
      pt: '60px',
    })}>
      {/* Fixed Header */}
      <Box sx={(theme) => ({ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        position: 'fixed',
        top: 0,
        left: isOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
        right: 0,
        width: `calc(100vw - ${isOpen ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH}px)`,
        zIndex: 1000,
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.95)' : '#fdfdfd',
        padding: '15px 24px',
        
      })}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={handleNavigateWithCheck}
            sx={(theme) => ({ 
              color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E293B',
              '& .MuiSvgIcon-root': {
                filter: `drop-shadow(0 0 8px ${theme.customPalette.glow.primary})`
              }
            })}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'end', gap: 2 }}>
            <Typography 
              variant="h4" 
              sx={(theme) => ({ 
                color: 'primary.main',
                fontWeight: 'bold'
              })}
            >
              {projectName}
            </Typography>
            <Typography 
              variant="caption" 
              sx={(theme) => ({ 
                color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                fontSize: '1rem',
                mb: -0.3
              })}
            >
              Created {new Date(createdAt).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ 
        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
        borderRadius: '8px 20px 0 0',
        boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 0 15px rgba(0,0,0,0.5)' : '0 0 15px rgba(0,0,0,0.1)',
        border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0,0,0,0.1)',
        mb: 0,
        backdropFilter: 'blur(10px)'
      }}>
        <StyledTabs value={currentTab} onChange={handleTabChangeWithCheck}>
          <StyledTab label="Overview" />
          <StyledTab label="History" />
          <StyledTab label="Settings" />
        </StyledTabs>
      </Box>
      
      {/* Content */}
      <Box sx={(theme) => ({ 
        bgcolor: theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
        borderRadius: '0 0 8px 8px',
        boxShadow: theme.palette.mode === 'dark' ? '0 0 15px rgba(0,0,0,0.5)' : '0 0 15px rgba(0,0,0,0.1)',
        border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0,0,0,0.1)',
        backdropFilter: 'blur(10px)',
        p: 3,
        maxHeight: 'calc(100vh - 160px)',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#c1c1c1',
          borderRadius: '4px',
          '&:hover': {
            background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#a8a8a8',
          },
        },
      })}>
        {/* Overview Tab */}
        {currentTab === 0 && (
          <Box sx={{ pl: '12px' }}>
            {/* Project Name Section */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={(theme) => ({ 
                    color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  })}
                >
                  Project Name
                </Typography>
                {!isEditing && (
                <IconButton
                  size="small"
                  onClick={() => canModify && setIsEditing(true)}
                  disabled={!canModify}
                  sx={(theme) => ({
                    color: canModify ? '#FF4800' : '#D1D5DB',
                    p: 0.5,
                    '&:hover': {
                      backgroundColor: canModify ? 'rgba(255, 72, 0, 0.1)' : 'transparent',
                    },
                    cursor: canModify ? 'pointer' : 'not-allowed',
                    '&.Mui-disabled': {
                      color: '#D1D5DB'
                    },
                    '& .MuiSvgIcon-root': {
                      filter: canModify ? `drop-shadow(0 0 8px ${theme.customPalette.glow.primary})` : 'none'
                    }
                  })}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                )}
              </Box>
              {isEditing ? (
                <>
<StyledInput
              value={editedData.projectName}
              onChange={(e) => {
                const value = e.target.value;
                setEditedData({ ...editedData, projectName: value });
                
                // Validate project name
                if (!value.trim()) {
                  setValidationErrors({ projectName: "Project name is required" });
                } else if (!/^[a-zA-Z0-9-_\s]+$/.test(value)) {
                  setValidationErrors({ projectName: "Project name can only contain letters, numbers, dashes, and underscores" });
                } else if (value.length > 50) {
                  setValidationErrors({ projectName: "Project name cannot exceed 50 characters" });
                } else {
                  setValidationErrors({});
                }
              }}
              sx={{ mb: validationErrors.projectName ? 1 : 2 }}
            />
{validationErrors.projectName && (
                <Typography 
                  sx={{ 
                    color: '#d32f2f',
                    fontSize: '0.75rem',
                    mt: 0.5,
                    ml: 1,
                    mb: 2
                  }}
                >
                  {validationErrors.projectName}
                </Typography>
              )}
                </>
              ) : (
                <Typography 
                  variant="body1"
                  sx={(theme) => ({ 
                    fontSize: '1rem',
                    color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#111827'
                  })}
                >
                  {projectName}
                </Typography>
              )}
            </Box>

            {/* Description Section */}
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme=> theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mb: 1
                }}
              >
                Description
              </Typography>
              {isEditing ? (
                <StyledTextArea
                  value={editedData.description}
                  onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
                />
              ) : (
                <Typography 
                  variant="body1"
                  sx={{ 
                    fontSize: '1rem',
                    color: 'dark:text-gray-100 text-gray-900'
                  }}
                >
                  {description || 'No description available'}
                </Typography>
              )}
            </Box>

            {/* Tags Section */}
            <Box sx={{ mb: 2 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme=> theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mb: 1
                }}
              >
                Tags
              </Typography>
              {isEditing ? (
                <Box >
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {editedData.tags.map((tag, index) => (
                      <Tag 
                        key={index} 
                        label={tag}
                        onDelete={() => {
                          setEditedData({
                            ...editedData,
                            tags: editedData.tags.filter((_, i) => i !== index)
                          });
                        }}
                      />
                    ))}
                  </Box>
                  <Box >
                    <Box 
                      sx={(theme) => ({ 
                        display: 'flex', 
                        alignItems: 'center',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.6)' : '#F3F4F6',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        gap: 1,
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB'}`,
                        width: '350px',
                        '&:focus-within': {
                          borderColor: '#FF4800',
                          boxShadow: '0 0 0 2px rgba(255, 72, 0, 0.1)',
                        }
                      })}
                    >
                      <StyledTagInput
                        placeholder="Press Enter to add tags"
                        onChange={(e) => {
                          const value = e.target.value;
                          const errorElement = document.getElementById('tag-error');
                          if (value.length > 20) {
                            if (errorElement) {
                              errorElement.textContent = 'Tag name cannot exceed 20 characters';
                            }
                          } else {
                            if (errorElement) {
                              errorElement.textContent = '';
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const value = (e.target as HTMLInputElement).value.trim();
                            if (handleAddTag(value)) {
                              (e.target as HTMLInputElement).value = '';
                            }
                          }
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          const input = e.currentTarget.previousSibling as HTMLInputElement;
                          const value = input.value.trim();
                          const errorElement = document.getElementById('tag-error');
                          
                          // Check length before trying to add
                          if (value.length > 20) {
                            if (errorElement) {
                              errorElement.textContent = 'Tag name cannot exceed 20 characters';
                            }
                            input.focus();
                            return;
                          }
                          
                          if (handleAddTag(value)) {
                            input.value = '';
                          }
                          input.focus();
                        }}
                        sx={(theme) => ({
                          padding: '4px',
                          color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                          minWidth: '24px',
                          minHeight: '24px',
                          '& .MuiSvgIcon-root': {
                            filter: `drop-shadow(0 0 8px ${theme.customPalette.glow.primary})`
                          },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                          }
                        })}
                      >
                        <SubdirectoryArrowLeftIcon sx={{ fontSize: 18 }} />
                      </IconButton>
</Box>
                  <Box sx={{ pl: '5px' }}>
                    <Typography
                      variant="caption"
                      sx={(theme) => ({
                        display: 'block',
                        mt: 1,
                        color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                        fontSize: '0.75rem'
                      })}
                    >
                      Press Enter or click the arrow to add a tag (max 10 tags, 20 chars each)
                    </Typography>
                    <Typography
                      id="tag-error"
                      variant="caption"
                      sx={(theme) => ({
                        display: 'block',
                        marginTop: '4px',
                        color: '#d32f2f',
                        fontSize: '0.75rem',
                        minHeight: '18px'
                      })}
                    />
                  </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {tags.length > 0 ? (
                    tags.map((tag, index) => (
                      <Tag key={index} label={tag} />
                    ))
                  ) : (
                    <Typography 
                      variant="body1"
                      sx={{ 
                        fontSize: '1rem',
                        color: '#6B7280'
                      }}
                    >
                      No tags available
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Repository URL Section */}
            <Box sx={{ mb: 3 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme=> theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mb: 1
                }}
              >
                Repository URL
              </Typography>
              <Box >
                <Typography 
                  variant="body1"
                  component="a"
                  href={repositoryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    fontSize: '1rem',
                    color: 'dark:text-gray-100 text-gray-900',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    display: 'block',
                    mb: 1
                  }}
                >
                  {repositoryUrl}
                </Typography>
                {isEditing && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#6B7280',
                      fontSize: '0.875rem',
                      display: 'block'
                    }}
                  >
                    Repository URL cannot be edited after project creation
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Last Scan Section */}
            <Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme=> theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mb: 0
                }}
              >
                Last Scan
              </Typography>
              {loading ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Box sx={(theme) => ({
                      width: '120px',
                      height: '24px',
                      borderRadius: '4px',
                      background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#E5E7EB',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                        '100%': { opacity: 1 },
                      },
                    })} />
                    <Box sx={(theme) => ({
                      width: '80px',
                      height: '24px',
                      borderRadius: '12px',
                      background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#E5E7EB',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    })} />
                  </Box>
                  <Box sx={(theme) => ({
                    width: '160px',
                    height: '20px',
                    borderRadius: '4px',
                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#E5E7EB',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  })} />
                </Box>
              ) : history.length > 0 ? (
                <Box >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography 
                      variant="body1"
                      sx={{ 
                        fontSize: '1rem',
                        color: 'dark:text-red-100 text-red-900'
                      }}
                    >
                      {(() => {
                        switch(history[0].scan_type.toLowerCase()) {
                          case 'threat-model': return 'Threat Model';
                          case 'sbom': return 'SBOM';
                          case 'sca': return 'SCA';
                          case 'secrets': return 'Secret Detection';
                          case 'sast': return 'SAST';
                          case 'dast': return 'DAST';
                          case 'infra': return 'Infrastructure';
                          default: return history[0].scan_type;
                        }
                      })()} Scan
                    </Typography>
                    <Chip
label={history[0].status.charAt(0).toUpperCase() + history[0].status.slice(1)}
                            size="small"
                            sx={(theme) => ({
                              height: '24px',
                              backgroundColor: history[0].status === 'completed'
                                ? theme.palette.mode === 'dark' ? 'rgba(22, 163, 74, 0.2)' : '#DCFCE7'
                                : history[0].status === 'failed'
                                ? theme.palette.mode === 'dark' ? 'rgba(220, 38, 38, 0.2)' : '#FEE2E2'
                                : theme.palette.mode === 'dark' ? 'rgba(234, 179, 8, 0.2)' : '#FEF9C3',
                              color: history[0].status === 'completed'
                                ? theme.palette.mode === 'dark' ? '#86EFAC' : '#166534'
                                : history[0].status === 'failed'
                                ? theme.palette.mode === 'dark' ? '#FECACA' : '#991B1B'
                                : theme.palette.mode === 'dark' ? '#FDE047' : '#854D0E',
                              fontWeight: 500,
                              fontSize: '0.75rem',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid',
                              borderColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.05)',
                              boxShadow: theme.palette.mode === 'dark'
                                ? 'none'
                                : '0 2px 4px rgba(0, 0, 0, 0.05)',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: theme.palette.mode === 'dark'
                                  ? 'none'
                                  : '0 4px 6px rgba(0, 0, 0, 0.1)'
                              },
                              '& .MuiChip-label': {
                                px: 1,
                                fontSize: '0.75rem'
                              }
                            })}
                    />
                  </Box>
                  <Typography 
                    variant="body2"
                    sx={{ 
                      color: '#6B7280'
                    }}
                  >
                    {new Date(history[0].created_at).toLocaleString()}
                  </Typography>
                </Box>
              ) : (
                <Typography 
                  variant="body1"
                  sx={{ 
                    fontSize: '1rem',
                    color: '#6B7280'
                  }}
                >
                  No scans performed yet
                </Typography>
              )}
            </Box>

            {/* Edit Actions */}
            {isEditing && (
              <Box sx={{ 
                position: 'sticky',
                bottom: 0,
                padding: '1rem',
                zIndex: 1000,
                marginLeft: -3,
                marginRight: -3,
                marginBottom: -3,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
              }}>
                <Button
                  onClick={() => {
                    if (hasUnsavedChanges()) {
                      setUnsavedChangesDialog(true);
                    } else {
                      setIsEditing(false);
                    }
                  }}
                  sx={{
                    color: '#6B7280',
                    '&:hover': {
                      backgroundColor: 'rgba(107, 114, 128, 0.1)',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saveLoading}
                  sx={{
                    backgroundColor: '#FF4800',
                    color: 'white',
                    px: 4,
                    '&:hover': {
                      backgroundColor: '#e64100',
                    },
                  }}
                >
                  {saveLoading ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    'Save changes'
                  )}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* History Tab */}
        {currentTab === 1 && (
          <Box sx={{ 
            position: 'relative',
            minHeight: '400px'
          }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} sx={{ color: '#FF4800' }} />
              </Box>
            
            ) : history.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: '#6B7280' }}>
                No history available
              </Box>
            ) : (
              <Box sx={{ pl: '12px' }}>
                {history.length > 0 && (
                  <Box sx={(theme) => ({
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    p: 1,
                    px: 2,
                    position: 'sticky',
                    top: -25,
                    backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : 'white',
                    zIndex: 1,
                  })}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={(theme) => ({ 
                        fontSize: '14px', 
                        color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280' 
                      })}>
                        Displaying {history.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}-{Math.min(currentPage * rowsPerPage, history.length)} of {history.length}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={(theme) => ({
                          color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#374151',
                          fontSize: '14px',
                          fontWeight: 500
                        })}>
                          Items per page:
                        </Typography>
                        <Select
                          value={rowsPerPage}
                          onChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          sx={(theme) => ({
                            height: '32px',
                            minWidth: '70px',
                            fontSize: '14px',
                            color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#374151',
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.6)' : 'white',
                            '.MuiSelect-select': {
                              paddingTop: '4px',
                              paddingBottom: '4px'
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#D1D5DB'
                            }
                          })}
                        >
                          <MenuItem value={5}>5</MenuItem>
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                        </Select>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {(() => {
                        const totalPages = Math.ceil(history.length / rowsPerPage);
                        let startPage = Math.max(currentPage - 2, 1);
                        let endPage = Math.min(startPage + 4, totalPages);
                        
                        if (endPage === totalPages) {
                          startPage = Math.max(endPage - 4, 1);
                        }

                        const pageNumbers = [];

                        if (startPage > 1) {
                          pageNumbers.push(1);
                          if (startPage > 2) {
                            pageNumbers.push('...');
                          }
                        }

                        for (let i = startPage; i <= endPage; i++) {
                          pageNumbers.push(i);
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pageNumbers.push('...');
                          }
                          pageNumbers.push(totalPages);
                        }

                        return pageNumbers.map((pageNum, index) => (
                          typeof pageNum === 'number' ? (
                            <Button
                              key={index}
                              onClick={() => setCurrentPage(pageNum)}
                              variant={currentPage === pageNum ? 'contained' : 'outlined'}
                              size="small"
                              sx={(theme) => ({
                                minWidth: '32px',
                                height: '32px',
                                padding: '0',
                                backgroundColor: currentPage === pageNum 
                                  ? '#FF4800' 
                                  : theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.6)' : 'white',
                                borderColor: currentPage === pageNum 
                                  ? '#FF4800' 
                                  : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#E5E7EB',
                                color: currentPage === pageNum 
                                  ? 'white' 
                                  : theme.palette.mode === 'dark' ? '#F1F5F9' : '#374151',
                                '&:hover': {
                                  backgroundColor: currentPage === pageNum 
                                    ? '#e64100' 
                                    : theme.palette.mode === 'dark' ? 'rgba(26, 26, 26, 0.8)' : '#F9FAFB',
                                  borderColor: currentPage === pageNum 
                                    ? '#e64100' 
                                    : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#D1D5DB'
                                }
                              })}
                            >
                              {pageNum}
                            </Button>
                          ) : (
                            <Box
                              key={index}
                              sx={(theme) => ({
                                display: 'flex',
                                alignItems: 'center',
                                px: 1,
                                color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280'
                              })}
                            >
                              {pageNum}
                            </Box>
                          )
                        ));
                      })()}
                    </Box>
                  </Box>
                )}
                <Box sx={{ position: 'relative', pl: '5px' }}>
                  {/* Timeline vertical line */}
                  {history.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage).length > 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '15px',
                        top: '25px',
                        height: 'calc(100% - 50px)',
                        width: '2px',
                        background: theme => theme.palette.mode === 'dark' 
                          ? 'linear-gradient(180deg, rgba(255, 72, 0, 0.5) 0%, rgba(255, 72, 0, 0.2) 100%)'
                          : 'linear-gradient(180deg, rgba(255, 72, 0, 0.3) 0%, rgba(255, 72, 0, 0.1) 100%)',
                      }}
                    />
                  )}
                {history
                  .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                  .map((item, index, slicedArray) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      mb: index === slicedArray.length - 1 ? 0 : 4,
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: theme => theme.palette.mode === 'dark' 
                          ? 'linear-gradient(135deg, #FF4800 0%, #FF6B00 100%)'
                          : '#FF4800',
                        border: theme => `2px solid ${theme.palette.mode === 'dark' ? '#1A1A1A' : 'white'}`,
                        boxShadow: theme => `0 0 10px ${theme.palette.mode === 'dark' ? 'rgba(255, 72, 0, 0.3)' : 'rgba(255, 72, 0, 0.2)'}`,
                        transition: 'all 0.2s ease-in-out',
                        ml: '5px',
                        '&:hover': {
                          transform: 'scale(1.2)',
                          boxShadow: theme => `0 0 15px ${theme.palette.mode === 'dark' ? 'rgba(255, 72, 0, 0.5)' : 'rgba(255, 72, 0, 0.4)'}`,
                        }
                      }}
                    />
                    <Box>
                      <Typography
                        sx={{
                          color: 'dark:text-gray-100 text-gray-900',
                          fontSize: '0.9375rem',
                          fontWeight: 400
                        }}
                      >
                        {(() => {
                          switch(item.scan_type.toLowerCase()) {
                            case 'threat-model': return 'Threat Model';
                            case 'sbom': return 'SBOM';
                            case 'sca': return 'SCA';
                            case 'secrets': return 'Secret Detection';
                            case 'sast': return 'SAST';
                            case 'dast': return 'DAST';
                            case 'infra': return 'Infrastructure';
                            default: return item.scan_type;
                          }
                        })()} Scan
                      </Typography>
                      <Typography
                        sx={{
                          color: '#6B7280',
                          fontSize: '0.875rem'
                        }}
                      >
                        {new Date(item.created_at).toLocaleString()}
                        {item.status && (
                          <Chip
                            label={item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                            size="small"
                            sx={(theme) => ({
                              ml: 2,
                              height: '24px',
                              backgroundColor: item.status === 'completed'
                                ? theme.palette.mode === 'dark' ? 'rgba(22, 163, 74, 0.2)' : '#DCFCE7'
                                : item.status === 'failed'
                                ? theme.palette.mode === 'dark' ? 'rgba(220, 38, 38, 0.2)' : '#FEE2E2'
                                : theme.palette.mode === 'dark' ? 'rgba(234, 179, 8, 0.2)' : '#FEF9C3',
                              color: item.status === 'completed'
                                ? theme.palette.mode === 'dark' ? '#86EFAC' : '#166534'
                                : item.status === 'failed'
                                ? theme.palette.mode === 'dark' ? '#FECACA' : '#991B1B'
                                : theme.palette.mode === 'dark' ? '#FDE047' : '#854D0E',
                              fontWeight: 500,
                              fontSize: '0.75rem',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid',
                              borderColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.05)',
                              boxShadow: theme.palette.mode === 'dark'
                                ? 'none'
                                : '0 2px 4px rgba(0, 0, 0, 0.05)',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: theme.palette.mode === 'dark'
                                  ? 'none'
                                  : '0 4px 6px rgba(0, 0, 0, 0.1)'
                              },
                              '& .MuiChip-label': {
                                px: 1,
                                fontSize: '0.75rem'
                              }
                            })}
                          />
                        )}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Settings Tab */}
        {currentTab === 2 && (
          <Box sx={{ pl: '12px' }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: 'dark:text-gray-100 text-gray-900',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  mb: 2
                }}
              >
                Delete project
              </Typography>
              <Typography
                sx={{
                  color: '#6B7280',
                  fontSize: '0.9375rem',
                  mb: 1
                }}
              >
                Deleting a project will:
              </Typography>
              <Typography
                component="div"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.9375rem',
                  mb: 0.5
                }}
              >
                1- Delete the project information and history from the project section.
              </Typography>
              <Typography
                component="div"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.9375rem',
                  mb: 0.5
                }}
              >
                2- Remove "Solve with Ana" feature from the Scan reports.
              </Typography>
              <Typography
                component="div"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.9375rem',
                  mb: 3
                }}
              >
                3- This will not affect your source code (deleting a project will not affect your originating source code repository).
              </Typography>
              <DeleteButton
                onClick={() => canModify && setDeleteDialogOpen(true)}
                disabled={!canModify}
                sx={{
                  backgroundColor: canModify ? "#FF4800" : "#D1D5DB",
                  cursor: canModify ? 'pointer' : 'not-allowed',
                  '&:hover': {
                    backgroundColor: canModify ? "#e64100" : "#D1D5DB"
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#D1D5DB',
                    color: '#9CA3AF'
                  }
                }}
              >
                Delete Project
              </DeleteButton>
            </Box>
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: theme => ({
              borderRadius: '8px',
              maxWidth: '400px',
              background: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.98)' : '#FFFFFF',
              boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`
            })
          }}
        >
          <DialogTitle sx={theme => ({ 
            pb: 1,
            color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#111827',
            fontSize: '1.25rem',
            fontWeight: 600
          })}>
            Delete Project?
          </DialogTitle>
          <DialogContent>
            <Typography sx={theme => ({ 
              color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', 
              fontSize: '0.9375rem' 
            })}>
              The project and all historical data associated with the project will be deleted from Ana. Once this project has been deleted it can not be recovered.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={theme => ({
                color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(107, 114, 128, 0.1)'
                }
              })}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDelete}
              disabled={deleteLoading}
              sx={{
                bgcolor: '#4F46E5',
                color: "white",
                textTransform: 'none',
                fontSize: '14px',
                height: 40,
                padding: '0 16px',
                '&:hover': {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#4338CA' : '#4F46E5'
                },
                '&:disabled': {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#374151' : '#E5E7EB',
                  color: theme => theme.palette.mode === 'dark' ? '#6B7280' : '#9CA3AF'
                }
              }}
            >
              {deleteLoading ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                'Delete'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Unsaved Changes Dialog */}
        <Dialog
          open={unsavedChangesDialog}
          onClose={() => setUnsavedChangesDialog(false)}
          PaperProps={{
            sx: theme => ({
              borderRadius: '8px',
              maxWidth: '400px',
              background: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.98)' : '#FFFFFF',
              boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`
            })
          }}
        >
          <DialogTitle sx={theme => ({ 
            pb: 1,
            color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#111827',
            fontSize: '1.25rem',
            fontWeight: 600
          })}>
            Unsaved changes detected
          </DialogTitle>
          <DialogContent>
            <Typography sx={theme => ({ 
              color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', 
              fontSize: '0.9375rem' 
            })}>
              You have unsaved changes. Would you like to save them before leaving?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              onClick={() => {
                setUnsavedChangesDialog(false);
                handleDiscard();
              }}
              sx={theme => ({
                color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(107, 114, 128, 0.1)'
                }
              })}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setUnsavedChangesDialog(false);
                handleSave();
              }}
              sx={{
                bgcolor: '#4F46E5',
                color: "white",
                textTransform: 'none',
                fontSize: '14px',
                height: 40,
                padding: '0 16px',
                '&:hover': {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#4338CA' : '#4F46E5'
                },
                '&:disabled': {
                  bgcolor: theme => theme.palette.mode === 'dark' ? '#374151' : '#E5E7EB',
                  color: theme => theme.palette.mode === 'dark' ? '#6B7280' : '#9CA3AF'
                }
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Save Confirmation Dialog */}
        <Dialog
          open={confirmSaveDialog}
          onClose={() => setConfirmSaveDialog(false)}
          PaperProps={{
            sx: theme => ({
              borderRadius: '8px',
              maxWidth: '400px',
              background: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.98)' : '#FFFFFF',
              boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`
            })
          }}
        >
          <DialogTitle sx={theme => ({ 
            pb: 1,
            color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#111827',
            fontSize: '1.25rem',
            fontWeight: 600
          })}>
            Confirm Save Changes
          </DialogTitle>
          <DialogContent>
            <Typography sx={theme => ({ 
              color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280', 
              fontSize: '0.9375rem' 
            })}>
              Are you sure you want to save these changes? Once saved, the changes will be applied and cannot be undozne.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              onClick={() => setConfirmSaveDialog(false)}
              sx={theme => ({
                color: theme.palette.mode === 'dark' ? '#94A3B8' : '#6B7280',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(107, 114, 128, 0.1)'
                }
              })}
            >
              Cancel
            </Button>
            <Button
            variant="contained"
              onClick={handleConfirmSave}
              disabled={saveLoading}
              sx={{
                bgcolor:'#4F46E5',
                color:"white",
                textTransform: 'none',
                fontSize: '14px',
                height: 40,
                padding: '0 16px',
                '&:hover': {
                  bgcolor:theme => theme.palette.mode === 'dark' ? '#4338CA' : '#4F46E5'
                },
                '&:disabled': {
              bgcolor: theme => theme.palette.mode === 'dark' ? '#374151' : '#E5E7EB',
              color: theme => theme.palette.mode === 'dark' ? '#6B7280' : '#9CA3AF'
            }
              }}
            >
              {saveLoading ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                'Save'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ProjectOverview;